import { useEffect, useState } from "react";
import { useRouterState } from "@tanstack/react-router";

/**
 * Individual tour pages only: `/tour/{slug}` and `/{lang}/tour/{slug}`.
 */
export function isTourDetailPath(pathname: string): boolean {
  return /^\/(?:[a-z]{2}\/)?tour\/[^/]+$/.test(pathname);
}

/**
 * Returns true when the bottom navigation should slide out of view:
 * only on individual tour pages, while the user scrolls down. Scrolling
 * up — or being near the top — brings it back.
 */
export function useNavAutohide(): boolean {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const enabled = isTourDetailPath(pathname);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setHidden(false);
      return;
    }
    let last = window.scrollY;
    let frame = 0;

    const evaluate = () => {
      frame = 0;
      const y = window.scrollY;
      const delta = y - last;
      // Ignore micro-movements / bounce so the bar never flickers.
      if (Math.abs(delta) < 8) return;
      if (y <= 40) setHidden(false);
      else if (delta > 0) setHidden(true);
      else setHidden(false);
      last = y;
    };

    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(evaluate);
    };

    setHidden(false);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [enabled, pathname]);

  return enabled && hidden;
}
